import { useThrottledCallback } from "use-debounce";
import { useUserRepository } from "../repositories/UserRepository";
import { UserData } from "./UserData"
import useLocalStorage from "react-use-localstorage";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { GameDataItem } from "./GameData";
import dayjs from "dayjs";
import { orderBy } from "lodash";
import { LOCAL_STORAGE_SUFFIX, UserPreferenceData } from "../PlayTracker";
import { add, EMPTY_SKILLS, Skills } from "./Skills";

export interface UsePlayDataProps {
    userData: UserData;
    gameLookup: Record<string, GameDataItem>;
    preferences?: UserPreferenceData;
}

export interface UsePlayDataResult {
    allPlays: readonly UserGamePlayedItem[];
    refreshPlayData: () => void;
    rankedSkillXps: Skills[];
}

interface StoredPlayData {
    allPlays: readonly UserGamePlayedItem[];
}

export interface MergeUserDataPlaysProps {
    existingPlays: readonly UserGamePlayedItem[];
    userData: UserData;
}

export interface UserGamePlayedItem {
    name: string;
    gameId: string;
    playId: string;
    playedOn: Date;
    users: GamePlayedItemUser[];
}

export interface GamePlayedItemUser {
    userId: string;
    userName: string;
    userIcon: string;
}

const DEFAULT_STORED_PLAY_DATA: StoredPlayData = { allPlays: [] } as const;

const normalizePlayedOn = (playedOn: Date | string): Date => {
    if (playedOn instanceof Date) {
        return playedOn;
    }

    return dayjs(playedOn).toDate();
};

export const mergeUserDataPlays = ({ existingPlays, userData }: MergeUserDataPlaysProps): readonly UserGamePlayedItem[] => {
    const currentUserPlayKeys = new Set(userData.gamesPlayed.map((gamePlayed) => `${gamePlayed.gameId}:${normalizePlayedOn(gamePlayed.playedOn).getTime()}`));
    const currentUserEntry = {
        userId: userData.userId,
        userName: userData.name,
        userIcon: userData.icon,
    };

    const mergedPlays = existingPlays.map((existingPlay) => ({ ...existingPlay, users: [...existingPlay.users] }));

    for (const gamePlayed of userData.gamesPlayed) {
        const userPlay: UserGamePlayedItem = {
            playId: gamePlayed.playId,
            playedOn: normalizePlayedOn(gamePlayed.playedOn),
            gameId: gamePlayed.gameId,
            name: gamePlayed.name,
            users: [currentUserEntry],
        };

        const matchingPlayIndex = mergedPlays.findIndex((existingPlay) => {
            if (existingPlay.playId && userPlay.playId && existingPlay.playId === userPlay.playId) {
                return true;
            }

            return existingPlay.gameId === userPlay.gameId && normalizePlayedOn(existingPlay.playedOn).getTime() === normalizePlayedOn(userPlay.playedOn).getTime();
        });

        if (matchingPlayIndex >= 0) {
            const existingPlay = mergedPlays[matchingPlayIndex];
            if (!existingPlay.users.some((existingUser) => existingUser.userId === userData.userId)) {
                mergedPlays[matchingPlayIndex] = {
                    ...existingPlay,
                    users: [...existingPlay.users, currentUserEntry],
                };
            }
            continue;
        }

        mergedPlays.push(userPlay);
    }

    const filteredPlays = mergedPlays
        .map((play) => {
            const shouldIncludeCurrentUser = currentUserPlayKeys.has(`${play.gameId}:${normalizePlayedOn(play.playedOn).getTime()}`);
            const nextUsers = shouldIncludeCurrentUser
                ? [...play.users.filter((user) => user.userId !== userData.userId), currentUserEntry]
                : play.users.filter((user) => user.userId !== userData.userId);

            if (!nextUsers.length) {
                return undefined;
            }

            return {
                ...play,
                users: nextUsers,
            };
        })
        .filter((play): play is UserGamePlayedItem => play !== undefined);

    return orderBy(filteredPlays, 'playedOn', 'desc');
};

export const usePlayData = ({ userData, gameLookup, preferences }: UsePlayDataProps): UsePlayDataResult => {
    const { listPlays: listPlaysImmediately, listUsers: listUsersImmediately } = useUserRepository();
    const listPlays = useThrottledCallback(listPlaysImmediately, 3000);
    const [storedPlayData, setStoredPlayData] = useLocalStorage(`play-tracker-play-data${LOCAL_STORAGE_SUFFIX}`, JSON.stringify(DEFAULT_STORED_PLAY_DATA));
    const playData: StoredPlayData = useMemo(() => !!storedPlayData ? JSON.parse(storedPlayData) : DEFAULT_STORED_PLAY_DATA, [storedPlayData]);
    const hasLoaded = useRef(false);
    const setPlayData = useCallback((newPlayData: StoredPlayData): void => {
        setStoredPlayData(JSON.stringify(newPlayData));
    }, [setStoredPlayData]);

    const listUsers = useThrottledCallback(listUsersImmediately, 3000);

    const fetchPlayData = useCallback(async () => {
        const users = await listUsers({ userId: userData.userId, userSecret: userData.userSecret });

        const plays = await listPlays({ userId: userData.userId, userSecret: userData.userSecret });
        const enrichedPlayData: readonly UserGamePlayedItem[] = plays?.map((play) => ({
            playId: play.playId,
            playedOn: dayjs(play.playedOn).toDate(),
            gameId: play.gameId,
            name: gameLookup[play.gameId]?.name,
            users: [{
                userId: play.userId,
                userName: users?.find((user) => user.userId === play.userId)?.name ?? '',
                userIcon: users?.find((user) => user.userId === play.userId)?.icon ?? '',
            }]
        })) ?? [];

        const collapsedPlayData: UserGamePlayedItem[] = [];
        let prevPlayData: UserGamePlayedItem | undefined;
        for (const playData of orderBy(enrichedPlayData, 'playedOn')) {
            const isPlayDataFromPrevGame = prevPlayData && prevPlayData.gameId === playData.gameId;
            const isPlayDataFromPrevUser = prevPlayData && playData.users.length === 1 && prevPlayData.users.some((user) => user.userId === playData.users[0].userId)
            if (prevPlayData && isPlayDataFromPrevGame && !isPlayDataFromPrevUser) {
                const mergedGame: UserGamePlayedItem = {
                    ...prevPlayData,
                    users: [...prevPlayData.users, ...playData.users]
                };
                collapsedPlayData.pop();
                collapsedPlayData.push(mergedGame);
                prevPlayData = mergedGame;
            } else {
                collapsedPlayData.push(playData);
                prevPlayData = playData;
            }
        }

        return orderBy(collapsedPlayData, 'playedOn', 'desc');
    }, [gameLookup, listPlays, listUsers, userData.userId, userData.userSecret]);

    const refreshPlayData = useCallback(() => {
        void (async () => {
            const collapsedPlayData = await fetchPlayData();
            const newStoredPlayData: StoredPlayData = {
                allPlays: orderBy(collapsedPlayData, 'playedOn', 'desc'),
            };
            setPlayData(newStoredPlayData);
        })();
    }, [fetchPlayData, setPlayData]);

    useEffect(() => {
        if (!hasLoaded.current && Object.keys(gameLookup).length > 0) {
            hasLoaded.current = true;
            refreshPlayData();
        }
    }, [refreshPlayData, gameLookup]);

    const allPlays = useMemo(() => mergeUserDataPlays({ existingPlays: playData.allPlays, userData }), [playData.allPlays, userData]);

    const filteredPlays = useMemo(() => {
        if (preferences?.yearFilter) {
            return allPlays.filter((play) => dayjs(play.playedOn).year().toString() === preferences.yearFilter);
        }
        return allPlays;
    }, [allPlays, preferences]);
    
    const rankedSkillXps = useMemo(() => {
        const playerXpMap: Record<string, Skills> = {};
        for (const play of filteredPlays) {
            for (const user of play.users) {
                if (!playerXpMap[user.userId]) {
                    playerXpMap[user.userId] = { ...EMPTY_SKILLS };
                }
                playerXpMap[user.userId] = add(playerXpMap[user.userId], gameLookup[play.gameId]?.xp ?? { ...EMPTY_SKILLS });
            }
        }

        const top3XpPerSkill: Skills[] = [ { ...EMPTY_SKILLS }, { ...EMPTY_SKILLS }, { ...EMPTY_SKILLS } ];
        for (const userId of Object.keys(playerXpMap)) {
            const userXp = playerXpMap[userId];
            for (const skill of Object.keys(userXp) as (keyof Skills)[]) {
                const xpValue = userXp[skill] ?? 0;
                if (xpValue > (top3XpPerSkill[0][skill] ?? 0)) {
                    top3XpPerSkill[2][skill] = top3XpPerSkill[1][skill];
                    top3XpPerSkill[1][skill] = top3XpPerSkill[0][skill];
                    top3XpPerSkill[0][skill] = xpValue;
                } else if (xpValue > (top3XpPerSkill[1][skill] ?? 0)) {
                    top3XpPerSkill[2][skill] = top3XpPerSkill[1][skill];
                    top3XpPerSkill[1][skill] = xpValue;
                } else if (xpValue > (top3XpPerSkill[2][skill] ?? 0)) {
                    top3XpPerSkill[2][skill] = xpValue;
                }
            }
        }
        return top3XpPerSkill;
    }, [filteredPlays, gameLookup]);

    return {
        allPlays: filteredPlays,
        refreshPlayData,
        rankedSkillXps,
    };
}
